import React, { useMemo, useState } from "react";
import { Box, Group, Stack } from "@mantine/core";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { TestimonialCard } from "./TestimonialCard";
import { Badge } from "./UI/Badge";
import { Heading2 } from "./UI/Typography";
import type { Testimonial } from "../data/types";

interface TestimonialWallProps {
  testimonials: Testimonial[];
  title?: string;
  showFilters?: boolean;
  availableTags?: string[];
  featuredOnly?: boolean;
  filterTags?: string[]; // Pre-filter by specific tags
}

export const TestimonialWall: React.FC<TestimonialWallProps> = ({
  testimonials,
  title,
  showFilters = false,
  availableTags = [],
  featuredOnly = false,
  filterTags = [],
}) => {
  const [selectedTags, setSelectedTags] = useState<string[]>(filterTags);

  // Filter testimonials
  const filteredTestimonials = useMemo(() => {
    let filtered = [...testimonials];

    // Filter by featured if needed
    if (featuredOnly) {
      filtered = filtered.filter((t) => t.featured);
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((t) =>
        selectedTags.some((tag) => t.tags.includes(tag))
      );
    }

    return filtered;
  }, [testimonials, featuredOnly, selectedTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
  };

  return (
    <Stack gap="xl">
      {(title || showFilters) && (
        <Stack gap="md">
          {title && (
            <Heading2 variant="shadow" mb="xs">
              {title}
            </Heading2>
          )}

          {showFilters && availableTags.length > 0 && (
            <Group gap="xs">
              <Box
                style={{ cursor: "pointer", display: "inline-block" }}
                onClick={clearFilters}
              >
                <Badge
                  variant={selectedTags.length === 0 ? "orange" : "outline"}
                >
                  All
                </Badge>
              </Box>
              {availableTags.map((tag) => (
                <Box
                  key={tag}
                  style={{ cursor: "pointer", display: "inline-block" }}
                  onClick={() => toggleTag(tag)}
                >
                  <Badge
                    variant={selectedTags.includes(tag) ? "orange" : "outline"}
                  >
                    {tag}
                  </Badge>
                </Box>
              ))}
              {selectedTags.length > 0 && (
                <Box
                  style={{ cursor: "pointer", display: "inline-block" }}
                  onClick={clearFilters}
                >
                  <Badge variant="light">Clear ({selectedTags.length})</Badge>
                </Box>
              )}
            </Group>
          )}
        </Stack>
      )}

      {filteredTestimonials.length === 0 ? (
        <Box
          style={{
            textAlign: "center",
            padding: "3rem",
            color: "rgba(255, 255, 255, 0.5)",
          }}
        >
          No testimonials found matching the selected filters.
        </Box>
      ) : (
        <ResponsiveMasonry
          columnsCountBreakPoints={{
            350: 1, // Mobile
            640: 2, // Tablet (medium)
            1024: 3, // Desktop (large)
            1280: 4, // Extra large
          }}
        >
          <Masonry gutter="8px">
            {filteredTestimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </Masonry>
        </ResponsiveMasonry>
      )}
    </Stack>
  );
};

export default TestimonialWall;
